import React, { useState, useMemo } from 'react';
import PayScheduleModal from '../../components/PayScheduleModal';
import ScheduleRow from './components/ScheduleRow';
import OverrideDateModal from './components/OverrideDateModal';
import { useSchedules } from './useSchedules';
import { formatDate } from './helpers';
import {
  BRAND, BRAND_DARK, BRAND_SOFT, BRAND_INK,
  INK, BLACK, TEXT_INK, BG_CARD, BG_PAGE, BORDER,
  DANGER_SOFT, DANGER_INK, DANGER_PILL_BG, DANGER_PILL_INK, DANGER_PILL_TEXT,
} from './styles';
import type { Schedule } from './types';

const PayScheduleModalAny = PayScheduleModal as any;

export default function PaySchedules() {
  const {
    schedules, isLoading, error, refetch,
    deleteSchedule, duplicateSchedule, togglePause, setDefault,
  } = useSchedules();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [overrideModal, setOverrideModal] = useState<Schedule | null>(null);

  const filteredSchedules = useMemo(() => schedules.filter(
    (s) => !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()),
  ), [schedules, searchQuery]);

  const totalEmployees = schedules.reduce((sum, s) => sum + (s.employee_count || 0), 0);
  const dueSchedules = schedules.filter((s) => s.is_due);
  const nextPayRun = schedules
    .filter((s) => s.next_pay_date && !s.is_paused)
    .sort((a, b) => (a.next_pay_date || '').localeCompare(b.next_pay_date || ''))[0];

  const handleEdit = (schedule: Schedule): void => {
    setEditingSchedule(schedule);
    setModalOpen(true);
    setOpenMenuId(null);
  };

  const handleModalClose = (refresh?: boolean): void => {
    setModalOpen(false);
    setEditingSchedule(null);
    if (refresh) refetch();
  };

  const handleDelete = async (id: string | number): Promise<void> => {
    if (!window.confirm('Delete this schedule? This cannot be undone.')) return;
    try {
      await deleteSchedule(id);
    } catch (err: any) {
      alert('Could not delete: ' + err.message);
    }
    setOpenMenuId(null);
  };

  const handleDuplicate = async (id: string | number): Promise<void> => {
    try {
      await duplicateSchedule(id);
    } catch (err: any) {
      alert('Could not duplicate: ' + err.message);
    }
    setOpenMenuId(null);
  };

  const handlePauseResume = async (schedule: Schedule): Promise<void> => {
    try {
      await togglePause(schedule);
    } catch (err: any) {
      alert('Could not ' + (schedule.is_paused ? 'resume' : 'pause') + ': ' + err.message);
    }
    setOpenMenuId(null);
  };

  const handleSetDefault = async (id: string | number): Promise<void> => {
    try {
      await setDefault(id);
    } catch (err: any) {
      alert('Could not set default: ' + err.message);
    }
    setOpenMenuId(null);
  };

  return (
    <div style={{
      maxWidth: '100%', margin: 0, padding: '28px 32px 90px',
      fontFamily: 'Inter, -apple-system, sans-serif',
    }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', marginBottom: 24,
      }}>
        <div>
          <div style={{
            fontSize: 12, fontWeight: 600, color: BRAND,
            letterSpacing: '0.5px', marginBottom: 6,
          }}>PAYROLL</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: BLACK, marginBottom: 4 }}>Pay schedules</div>
          <div style={{ fontSize: 14, color: TEXT_INK }}>Define how and when your team gets paid</div>
        </div>
        <button
          onClick={() => { setEditingSchedule(null); setModalOpen(true); }}
          style={{
            background: INK, color: 'white',
            fontSize: 14, fontWeight: 600,
            padding: '10px 18px', borderRadius: 10,
            cursor: 'pointer', border: 'none',
            display: 'flex', alignItems: 'center', gap: 6,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          <span style={{ fontSize: 16 }}>+</span> New schedule
        </button>
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <div style={{ background: BG_PAGE, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 12, color: TEXT_INK, marginBottom: 4 }}>Total schedules</div>
          <div style={{
            fontSize: 24, fontWeight: 600, color: BLACK, fontVariantNumeric: 'tabular-nums',
          }}>{schedules.length}</div>
        </div>
        <div style={{ background: BG_PAGE, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 12, color: TEXT_INK, marginBottom: 4 }}>Employees on schedules</div>
          <div style={{
            fontSize: 24, fontWeight: 600, color: BLACK, fontVariantNumeric: 'tabular-nums',
          }}>{totalEmployees}</div>
        </div>
        <div style={{ background: nextPayRun ? BRAND_SOFT : BG_PAGE, borderRadius: 10, padding: 16 }}>
          <div style={{
            fontSize: 12, color: nextPayRun ? BRAND_DARK : TEXT_INK, marginBottom: 4,
          }}>Next pay run</div>
          <div style={{
            fontSize: 16, fontWeight: 600, color: nextPayRun ? BRAND_INK : TEXT_INK,
          }}>
            {nextPayRun ? formatDate(nextPayRun.next_pay_date) : '-'}
          </div>
          {nextPayRun && (
            <div style={{ fontSize: 11, color: BRAND_DARK }}>{nextPayRun.name}</div>
          )}
        </div>
        <div style={{
          background: dueSchedules.length > 0 ? DANGER_PILL_BG : BG_PAGE,
          borderRadius: 10, padding: 16,
        }}>
          <div style={{
            fontSize: 12,
            color: dueSchedules.length > 0 ? DANGER_PILL_TEXT : TEXT_INK,
            marginBottom: 4,
          }}>Currently due</div>
          <div style={{
            fontSize: 24, fontWeight: 600,
            color: dueSchedules.length > 0 ? DANGER_PILL_INK : TEXT_INK,
            fontVariantNumeric: 'tabular-nums',
          }}>{dueSchedules.length}</div>
        </div>
      </div>

      {/* Search */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 14,
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: BLACK }}>Your schedules</div>
        <input
          type="text"
          placeholder="Search schedules"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '8px 12px', border: '1px solid ' + BORDER, borderRadius: 8,
            fontSize: 13, width: 240, fontFamily: 'inherit', color: TEXT_INK,
          }}
        />
      </div>

      {/* States */}
      {isLoading && (
        <div style={{ padding: 40, textAlign: 'center', color: TEXT_INK }}>Loading schedules...</div>
      )}

      {!isLoading && error && (
        <div style={{
          padding: 16, background: DANGER_SOFT, borderRadius: 10,
          color: DANGER_INK, fontSize: 13,
        }}>
          Could not load schedules. {error.message}
        </div>
      )}

      {!isLoading && !error && filteredSchedules.length === 0 && (
        <div style={{
          padding: 48, textAlign: 'center',
          background: BG_PAGE, borderRadius: 12,
        }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: BLACK, marginBottom: 8 }}>
            No pay schedules yet
          </div>
          <div style={{ fontSize: 14, color: TEXT_INK, marginBottom: 20 }}>
            Add your first schedule to start running payroll
          </div>
          <button
            onClick={() => { setEditingSchedule(null); setModalOpen(true); }}
            style={{
              background: INK, color: 'white',
              fontSize: 14, fontWeight: 600,
              padding: '10px 20px', borderRadius: 10,
              cursor: 'pointer', border: 'none',
            }}
          >
            Add your first schedule
          </button>
        </div>
      )}

      {!isLoading && !error && filteredSchedules.length > 0 && (
        <div style={{ border: '1px solid ' + BORDER, borderRadius: 12 }}>
          {filteredSchedules.map((schedule, idx) => (
            <ScheduleRow
              key={String(schedule.id)}
              schedule={schedule}
              isLast={idx === filteredSchedules.length - 1}
              isMenuOpen={openMenuId === schedule.id}
              onToggleMenu={setOpenMenuId}
              onEdit={handleEdit}
              onOverride={setOverrideModal}
              onDuplicate={handleDuplicate}
              onSetDefault={handleSetDefault}
              onTogglePause={handlePauseResume}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Edit/new schedule modal */}
      {modalOpen && (
        <PayScheduleModalAny
          schedule={editingSchedule}
          onClose={handleModalClose}
        />
      )}

      {/* Override date modal */}
      {overrideModal && (
        <OverrideDateModal
          schedule={overrideModal}
          onClose={() => setOverrideModal(null)}
          onSaved={() => {
            setOverrideModal(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}